// ─── The committee's home screen ─────────────────────────────────────────────
//
// Drea's own list of seven — Records, Assets, Repairs, Funding, Contractors,
// Events, Reports — plus Decisions and Compliance, which she specified
// afterwards.
//
// ── Rebuilt on the real design language, 2026-09-19 ──────────────────────────
//
// This screen used to be `@nulife/home-ui`'s HomeShell/HomeTile — a second
// home-screen system living in the platform beside Farndale's. Drea, verbatim:
// "EVERYTHING SHOULD BE DESIGNED THE SAME - only the tile names change, and
// the hero."
//
// ── Moved onto `@nulife/chassis`, 2026-09-24 (Stage 4) ───────────────────────
//
// It then rendered through `@nulife/design-language` — Farndale's real
// components — but this file still ASSEMBLED them: its own AppFrame, its own
// Hero, its own PulseStrip, its own ModuleStrip, its own grid, its own ADD
// sheet. That assembly step is where every divergence between this app and
// Farndale actually happened, and it is now gone. `<NulifeApp>` renders the
// whole shell; this file supplies content and nothing else.
//
// Two bugs Drea found from real screenshots close as a consequence rather than
// as separate patches:
//
//   1. INVENTED TILE ARTWORK. Eight motifs — messages, proposals, documents,
//      compliance, minutes, funding, contacts, reports — were placeholder
//      line drawings somebody here drew "in Farndale's idiom". The chassis
//      does not have them and must not. A concept with no real Sol artwork
//      now uses `motif: "none"`, which is Farndale's own nothing-yet state:
//      `Watermark()` opens `if (motif === "none") return null;`. No drawing.
//   2. THE CSS RESET. The gutters and the second scrollbar came from
//      `@nulife/design-language/styles.css` deliberately omitting Preflight.
//      The chassis ships the complete reset, so consuming it is sufficient.
//
// What is left below is genuinely this product: the tile registry, the pulse
// items, the Add destinations, the hero's title and logo, and the live numbers
// behind each tile. Nothing about the visual system is decided in this file —
// there is no longer anywhere in it to decide one.

import { useMemo } from "react";
import {
  NulifeApp, AskMiraContext,
  type AskMiraHandler, type ChassisTile,
  type LoadState, type LiveStatusRow, type PulseItem, type WatermarkMotif,
} from "@nulife/chassis";
import { useAsync, ApiError } from "@nulife/app-ui";
import { useLiveRefresh } from "./useLiveRefresh.js";
import * as api from "./api.js";

/**
 * The tiles this product offers.
 *
 * Farndale's registry carries the note that its seven "are one configuration
 * of the platform, not the platform's ceiling". This is the second
 * configuration, and it is the whole of what makes this home screen Memorial
 * Hall's rather than the show's.
 *
 * `path` and `comingSoon` are exclusive: a tile either goes somewhere real or
 * says it does not exist yet. There is no third state where it looks live and
 * leads nowhere.
 */
const TILE_REGISTRY: Record<string, {
  label: string;
  motif: WatermarkMotif;
  path?: string;
  comingSoon?: boolean;
  hint?: string;
}> = {
  // ── Artwork: real, or nothing. There is no third option ─────────────────
  //
  // CORRECTED 2026-09-24. Eight of these carried motifs — `messages`,
  // `proposals`, `documents`, `compliance`, `minutes`, `funding`, `contacts`,
  // `reports` — that do not exist in Farndale. They were placeholder line
  // drawings made here on 2026-09-21, after Drea asked for a generic
  // placeholder for REPAIRS specifically and that got generalised to all eight
  // gaps. Faithful to the idiom, honestly labelled in a comment, and still an
  // invention: Drea found them from a real screenshot, the third time this
  // class of thing happened, and they are a large part of why the chassis
  // exists at all.
  //
  // So: a tile carries Sol's REAL Farndale artwork when the subject genuinely
  // IS the same thing, and `"none"` otherwise. `"none"` is not a gap waiting
  // to be filled with something drawn here — it is Farndale's own treatment
  // for a concept with no art, and `Watermark()` renders literally nothing for
  // it. Real icons from Sol are one batched item, not nine blockers.
  //
  // Real, and why: Members is Farndale's two figures, Tasks its clipboard of
  // ticks, Events its timetable grid, Assets its own geometric motif, Repairs
  // the `general` cog Drea asked for by name (2026-09-21).
  // ── SCOPED DOWN FOR THE DEMO, 2026-09-27 ─────────────────────────────────
  //
  // Drea, 2026-09-27 (widened from the earlier three): Members, Tasks,
  // Messages, Proposals and Documents are in scope and must reach real
  // Farndale parity. Everything else shows "Coming soon" REGARDLESS OF ITS
  // CURRENT STATE — so Events, Compliance and Minutes Capture are marked
  // coming-soon while the module behind each is built and working. That is
  // deliberate, and the `path` stays so restoring one is deleting two words.
  messages:    { label: "MESSAGES",    motif: "none",     path: "/messages" },
  // REAL as of 2026-09-22 — `@nulife/tasks`, the shared package, not a
  // committee-shaped copy of Farndale's.
  tasks:       { label: "TASKS",       motif: "tasks",    path: "/tasks" },
  members:     { label: "MEMBERS",     motif: "stewards", path: "/members" },
  // RENAMED 2026-09-19 (Drea): the label only. Key and route are unchanged,
  // because both are stored and linked-to — `/records` still opens the library.
  records:     { label: "DOCUMENTS",   motif: "none",     path: "/records" },
  decisions:   { label: "PROPOSALS",   motif: "none",     path: "/decisions" },
  // REAL as of 2026-10-02 — `@nulife/discussion`, step 1 of the Discussion →
  // Decisions direction. A place to talk something through without a vote,
  // which is the thing Proposals was being made to do badly.
  // RENAMED 2026-10-03 (Drea): "FORUM", to match the screen it opens since
  // the Forum was built on it. The label only — the key and `/discussion`
  // stay, because both are stored and linked to (a Task's "Raised in
  // discussion" points at `/discussion/<id>`), the same as Records above.
  // RENAMED AGAIN 2026-10-04 (Drea, confirmed directly): "MAIN DISCUSSIONS".
  // A deliberate second change of mind, not a slip. Label only, as above.
  discussion:  { label: "MAIN DISCUSSIONS", motif: "none", path: "/discussion" },
  // ADDED 2026-10-25 (Drea, via C1): label "OUR MEDIA". Label only was given:
  // no artwork ("none", never an invented drawing) and the position in
  // HOME_TILES is the brief's call, one line to move.
  media:       { label: "OUR MEDIA",   motif: "none",     path: "/media" },
  compliance:  { label: "COMPLIANCE",  motif: "none",     path: "/compliance", },
  // MINUTES since 2026-10-03 (Drea): the simple tile — drop the file in, tick
  // it agreed, it goes to the committee. Minutes Capture, the recording
  // version this tile used to open, is paused and dormant at /minutes-capture
  // with no tile, while she decides whether to build it at all.
  minutes:     { label: "MINUTES",     motif: "none",     path: "/minutes", },
  // RENAMED 2026-10-04 (Drea): "CALENDAR". It always opened `/calendar`, the
  // hall's general calendar; the label was what was wrong. Key unchanged.
  events:      { label: "CALENDAR",    motif: "schedule", path: "/calendar", },
  // REAL as of 2026-10-03 — `@nulife/booking`, the module Crafting Comb runs.
  // Admin and Secretary only (Drea): left out of the grid for anybody else
  // (see `tilesFor`), and every bookings route refuses them regardless.
  bookings:    { label: "BOOKINGS",    motif: "none",     path: "/bookings", },
  // NEW 2026-10-04 (Drea). Holds only what is real today: the hall's hire
  // types and opening hours, the same screen as Bookings → Setup. Nothing is
  // invented to fill it. Admin and Secretary only, like Bookings, because
  // that is who may change those, and a tile that opened onto a refusal
  // would be a doorway onto a wall.
  setup:       { label: "SETUP",       motif: "none",     path: "/setup", },

  // ── Nothing behind them yet, and they say so ────────────────────────────

  assets:      { label: "ASSETS",      motif: "assets",   comingSoon: true, hint: "What the hall owns, and where it is" },
  repairs:     { label: "REPAIRS",     motif: "general",  comingSoon: true, hint: "What needs doing, and what was done" },
  funding:     { label: "FUNDING",     motif: "none",     comingSoon: true, hint: "Grants, applications and deadlines" },
  contractors: { label: "CONTRACTORS", motif: "none",     comingSoon: true, hint: "Who to call, and who was called" },
  reports:     { label: "REPORTS",     motif: "none",     comingSoon: true, hint: "Usage, spending and the year in summary" },
};

/**
 * The order they appear in — Drea's, 2026-09-19.
 *
 * She asked for Messages, Tasks and Proposals first in that order, with
 * Members "alongside them, prominently". The grid is two columns wide (it is
 * Farndale's grid, and a phone will not carry three of these tiles across), so
 * those four are the first four: Messages and Tasks on the first row,
 * Proposals and Members on the second. If she meant a literal three-across
 * row, that is a `grid-cols-3` and this list unchanged.
 *
 * Everything real follows, then everything that is not built. The split is not
 * decoration: it puts what works at the top of a phone screen.
 */
/**
 * ── The grid, 2026-09-27 ─────────────────────────────────────────────────
 *
 * MESSAGES and TASKS are no longer here. Both sit in the top pulse strip
 * with live counts, so a big tile underneath was a second door to the same
 * room — Drea's call, and she confirmed it after a correction.
 *
 * PROPOSALS keeps both, deliberately: she did not ask for it to go, despite
 * it also being in the strip.
 *
 * MEMBERS is last, on her instruction — an ordinary tile, nothing else about
 * it changed, just lowest priority.
 *
 * ── One thing worth her eye, taken literally rather than guessed ──────────
 *
 * "Bottom of the tile grid order" puts MEMBERS below the coming-soon tiles,
 * which cuts against the rule this list used to follow: everything real
 * first, everything unbuilt after, so what works is at the top of a phone
 * screen. So a working tile now sits under eight that say "Coming soon",
 * which may read as a mistake to somebody seeing it for the first time.
 *
 * Built as asked rather than adjusted on my own judgement. Moving it to just
 * after PROPOSALS — bottom of the REAL tiles instead of bottom of the list —
 * is one line if that is what she meant.
 */
/**
 * ── Back off the grid, 2026-09-28 ───────────────────────────────────────
 *
 * The 2026-09-27 reopening above was explicit that it was a one-day
 * override for Drea's presentation and should not become the new default.
 * It was never reverted afterward -- Drea found MESSAGES/TASKS/PROPOSALS
 * still showing as home tiles today, across three browsers and her phone,
 * and confirmed she wants the pre-presentation state back: MESSAGES and
 * TASKS live in the pulse strip only (real counts, no second door to the
 * same room); PROPOSALS ([decisions]) stays in the grid, as it always was
 * meant to.
 */
const HOME_TILES = [
  // 2026-10-02: DISCUSSION first among the real tiles. It is the new entry
  // point of the direction Proposals is being folded into, and it is the
  // only one of these somebody arrives at without already knowing what they
  // want — a tile is how it gets found. Not added to the pulse strip: that
  // is a surface Drea curated by hand, and it carries live counts this
  // module does not produce yet.
  "discussion",
  // 2026-10-04, Drea's order for the six she named: Main Discussions →
  // Calendar → Documents → Minutes → Compliance → Members. Bookings sits
  // right after Calendar (related; the brief's call, not hers, so one line to move)
  // and Setup closes the real tiles. The coming-soon tiles stay at the end.
  "events", "bookings",
  "records", "media", "minutes", "compliance", "members",
  "setup",
  "assets", "repairs", "funding", "contractors", "reports",
]; // 2026-09-28 (later): PROPOSALS off the grid too, per Drea -- pulse strip only, same as Messages/Tasks

/** The persistent strip: the modules that actually exist. A coming-soon tile
 *  has no business here — the strip is for getting back to a module, and there
 *  is nothing to get back to. */
/** The persistent strip: the modules that actually exist AND are in scope.
 *  A strip entry into a coming-soon tile is the doorway-onto-a-wall rule
 *  broken from the other side — the tile says "not yet" and the strip walks
 *  you straight in. Trimmed for the demo alongside the tiles (2026-09-27);
 *  the names come back when the tiles do. */
const MODULE_TILES = ["messages", "members", "tasks", "decisions", "records"];

/**
 * Where each ADD action actually goes.
 *
 * Farndale's rule, carried over: "A doorway that opens onto a wall is worse
 * than no doorway: it teaches people the control is decorative." Every entry
 * here is an existing screen with a real creation control on it — the
 * Decisions list has Raise a matter, the library has upload, the calendar has
 * add-an-entry, Compliance has add-a-requirement. None of them is a new screen
 * invented to fill the menu.
 */
const ADD_DESTINATIONS: { id: string; label: string; path: string; adminOnly?: boolean }[] = [
  // Through the existing menu rather than step 2's unified "+Add → pick a
  // kind" picker, which is explicitly not this pass. One more entry in a
  // list that already works is not that picker.
  { id: "discussion",  label: "Something to talk through",  path: "/discussion" },
  { id: "proposal",    label: "A matter for the committee", path: "/decisions" },
  { id: "document",    label: "A document to file",         path: "/records" },
  // An event and a compliance obligation both work, and both open a tile that
  // says "Coming soon" — offering them would be the doorway-onto-a-wall the
  // comment above warns about. They come back with their tiles.
];

export function Home({ me, config, onGo, onSignedOut, assistantUnavailable }: {
  me: api.Me;
  config: api.Config;
  /** Asked once in App.tsx and passed to every framed screen, so they cannot
   *  disagree about whether the assistant is connected. */
  assistantUnavailable?: string;
  /** Navigate in the app. */
  onGo: (path: string) => void;
  onSignedOut: () => void;
}) {
  /**
   * What is waiting, from the shared alert store.
   *
   * A failure here shows NO badge rather than a zero: "nothing waiting" is a
   * claim, and making it because a request failed is the confabulation this
   * platform is built against. `useAsync` keeps the three states apart, and
   * `data` stays null on failure, so every count below is simply absent.
   */
  const alerts = useAsync(() => api.getAlerts(), []);
  const waiting = (kind: string) =>
    alerts.data?.alerts.filter(a => a.resourceKind === kind).length;

  /** Open matters. Only the count — "how many are open" is what would change
   *  what somebody does next; the list itself is one tap away. */
  const openMatters = useAsync(() => api.getProposals({ status: "open" }), []);

  /** Unread messages, from `@nulife/messaging`'s own inbox count — the same
   *  number the Messages screen shows beside each conversation, so a badge and
   *  the screen behind it cannot disagree. */
  const unread = useAsync(() => api.getUnread(), []);
  // Live while the page is open (Drea, 2026-10-07), not only on arrival.
  useLiveRefresh(unread.reload);

  /** Documents that need action, counted the way their cards say it
   *  (2026-10-07), not from the alert list. */
  const documentsNeedingAction = useAsync(() => api.getDocumentsNeedingAction(), []);

  /** Who is on the committee, from `@nulife/people`. */
  const members = useAsync(() => api.getMembers(), []);

  /**
   * Planned work, from `@nulife/tasks`. Two numbers because they answer
   * different questions: what is outstanding at all, and what is on me.
   *
   * NOT COMPLETE, not `status: "open"` — corrected 2026-09-24. `TASK_STATUSES`
   * is `["open", "active", "complete"]`, so filtering to "open" silently drops
   * every task somebody has actually started, and "Open: 3" would fall to 2
   * the moment work began on one. Farndale counts `status !== "complete"` and
   * this now matches it.
   *
   * `mine` resolves to `forPersonId` plus this person's groups on the server —
   * the same "you or your group hold it" rule Farndale uses — and it has to be
   * filtered here too: unfiltered it counted completed tasks as still yours.
   */
  /**
   * ── Tasks may legitimately be FORBIDDEN, and that is not an error ─────────
   *
   * Drea, 2026-09-28: Tasks are for Committee, Volunteer and Staff. A Guest —
   * or anybody with no tier recorded — gets a 403 from `/business/tasks`.
   *
   * Without this, that 403 became `allTasks.error`, which turned `pulseState`
   * to "unavailable" and blanked the WHOLE strip: Messages and Proposals
   * showed em dashes too, because one of seven sources was refused. Caught by
   * the browser suite's unread-count test, which is about Messages and has
   * nothing to do with tiers — the blast radius was the point.
   *
   * So a refusal resolves to `null` rather than throwing. `null` means "not
   * for you" and removes the Tasks item from the strip entirely; `undefined`
   * still means "not supplied" and renders as an em dash. Three states,
   * because there are three facts.
   */
  const notForYou = (err: unknown) => {
    if (err instanceof ApiError && err.status === 403) return null;
    throw err;
  };
  const allTasks = useAsync(() => api.getTasks().catch(notForYou), []);
  const mineTasks = useAsync(() => api.getTasks({ mine: true }).catch(notForYou), []);
  const tasksAreYours = allTasks.data !== null;
  const openTaskCount = allTasks.data?.filter(t => t.status !== "complete").length;
  const myTaskCount = mineTasks.data?.filter(t => t.status !== "complete").length;

  /** What the committee is waiting on this person for — the pulse strip's
   *  "Yours" for PROPOSALS. See `countAwaitingVoteFrom`. */
  const myMatters = useAsync(() => api.getMyDecisionLoad(), []);

  const compliancePort = useMemo(() => api.compliancePort(), []);
  const complianceSummary = useAsync(() => compliancePort.summary(), [compliancePort]);

  /**
   * Today in the hall — the Live Status strip's real contents.
   *
   * Farndale's hero shows what is running in each ring right now. The hall's
   * equivalent is what the room is booked for today, and it comes from the
   * same calendar the Events tile opens, so the two can never disagree.
   *
   * `failures` is why this is not just a try/catch: the calendar answers with
   * the sources that failed rather than throwing, and a hall that rendered an
   * empty day because a source was down would be claiming nobody is booked.
   */
  const today = useMemo(() => {
    const from = new Date(); from.setHours(0, 0, 0, 0);
    const to = new Date(from); to.setDate(to.getDate() + 1);
    return { from, to };
  }, []);
  const calendarPort = useMemo(() => api.calendarPort(config.timeZone), [config.timeZone]);
  const diary = useAsync(() => calendarPort.list(today.from, today.to), [calendarPort, today]);

  const liveStatus: LoadState<LiveStatusRow[]> =
    diary.loading && !diary.data ? { status: "loading" }
    : diary.error ? { status: "unavailable", reason: diary.error }
    // A source that failed is named rather than silently dropped — the day is
    // incomplete, and an incomplete day shown as a complete one is the claim
    // this product exists not to make.
    : diary.data && diary.data.failures.length > 0 ? {
        status: "unavailable",
        reason: `part of the diary is missing (${diary.data.failures.map(f => f.source).join(", ")})`,
      }
    : !diary.data || diary.data.items.length === 0 ? { status: "empty" }
    : {
        status: "ready",
        data: [{
          id: "hall",
          // Short on purpose. The lead label sits to the LEFT of the bar and
          // takes its width from the text, so "Farndale Memorial Hall" left the
          // timeline itself about a centimetre wide on a phone. Farndale's own
          // labels are "FIELD 1", "RING 2" — the room, not the organisation,
          // which is already the title two lines above.
          label: "The hall",
          segments: diary.data.items
            .filter(i => !i.allDay)
            .map(i => ({ id: i.id, label: i.title, startsAt: i.startsAt, endsAt: i.endsAt })),
        }],
      };

  /**
   * The pulse strip.
   *
   * Farndale's Texts and Calls do not exist here, so this is the hall's own
   * set. The rule they are chosen by is Farndale's: "a tile or strip only gets
   * a live number when that number would change what someone does next."
   *
   * Every number is real or absent. `undefined` renders as an em dash, not a
   * zero — "not supplied" and "none" are different facts.
   */
  const pulseState: "ready" | "loading" | "unavailable" =
    alerts.loading || openMatters.loading || complianceSummary.loading
      || myMatters.loading || allTasks.loading || mineTasks.loading || unread.loading ? "loading"
    : alerts.error || openMatters.error || complianceSummary.error
      || myMatters.error || allTasks.error || mineTasks.error || unread.error ? "unavailable"
    : "ready";

  const mustAck = (alerts.data?.alerts ?? []).filter(a => a.needsAck && !a.acknowledged);

  /**
   * The top strip: Messages, Tasks, Proposals, and nothing else.
   *
   * Drea, 2026-09-21 — "ONLY Messages, Tasks, and Proposals, nothing else in
   * that top row." Alerts, Compliance and Documents came out; they still have
   * their own tiles below.
   *
   * Which items the strip carries is configuration in Farndale's own design
   * ("Farndale's set is one configuration of the platform, not its ceiling"),
   * so this changes content and nothing about the component.
   */
  /**
   * ── General / Yours, both rows, always (Drea, 2026-09-24) ─────────────────
   *
   * Farndale's strip carries two rows on every tile and this one carried one.
   * The rule the two halves mean, from Farndale's own server: "General is what
   * is out there; Yours is what is addressed to you. The second is the one
   * that changes what somebody does next."
   *
   * Both rows are now unconditional. They used to be suppressed at zero, which
   * is wrong for this pair specifically: "Yours: 0" is a real and useful
   * answer — nothing is waiting on you — and a row that vanishes makes the
   * tile a different shape depending on the data, which is exactly the
   * asymmetry Farndale's `items-stretch` exists to avoid. An absent count is
   * still different from a zero: a number the server did not send renders as
   * an em dash, because `undefined` reaches `Metric` untouched.
   *
   * ── What "Yours" honestly means per tile ──────────────────────────────────
   *
   * Farndale does not use one mechanical rule either — it asks, per type, what
   * this person is actually on the hook for:
   *
   *   MESSAGES   General = conversations you are in, Yours = unread
   *              PERSONAL messages sent directly to you. Group messages are
   *              the group's, not specifically yours, so they are left out
   *              (Drea, 2026-10-07). The server computes both from `listInbox`
   *              for the signed-in person.
   *   TASKS      General = every task not complete, Yours = those held by you
   *              or a group you are in. Farndale's rule exactly.
   *   PROPOSALS  General = open matters, Yours = the ones still waiting on
   *              YOUR vote. Not the ones you raised: those are waiting on
   *              everybody else, so they are not what changes what you do
   *              next. Uses the decisions module's own `awaiting` definition
   *              so this and the proposal screen cannot disagree.
   */
  const pulseItems: PulseItem[] = [
    {
      id: "messages",
      label: "MESSAGES",
      metrics: [
        { label: "General", value: unread.data?.threads },
        // Yours = personal messages sent directly to you (Drea, 2026-10-07).
        { label: "Yours", value: unread.data?.direct },
      ],
      onPress: () => onGo("/messages"),
    },
    // Absent rather than empty for somebody who may not see Tasks. A strip
    // item reading "TASKS — —" that opens onto a refusal is the doorway onto
    // a wall Drea ruled out on 2026-08-16.
    ...(tasksAreYours ? [{
      id: "tasks",
      label: "TASKS",
      metrics: [
        { label: "General", value: openTaskCount },
        { label: "Yours", value: myTaskCount },
      ],
      onPress: () => onGo("/tasks"),
    }] : []),
    {
      id: "decisions",
      label: "PROPOSALS",
      metrics: [
        { label: "General", value: openMatters.data?.length },
        { label: "Yours", value: myMatters.data?.awaitingMyVote },
      ],
      onPress: () => onGo("/decisions"),
    },
  ];


  /**
   * The grid, as content.
   *
   * Every field here is a fact about this committee — a name, a picture, a
   * destination, a live number. There is deliberately nothing in a
   * `ChassisTile` that can say how a tile LOOKS, which is the whole point of
   * the migration: the previous version of this file built the grid itself and
   * could therefore get it wrong.
   *
   * `href` as well as `onPress`: the anchor keeps middle-click and
   * open-in-new-tab working, the handler keeps the plain click in-app. A tile
   * with no module behind it gets neither, and the chassis enforces that.
   */
  /** Bookings is only a tile for whoever may open it. A tile that answers
   *  "forbidden" is a doorway onto a wall. */
  const visibleTiles = HOME_TILES.filter(id =>
    (id !== "bookings" && id !== "setup") || me.canManageBookings === true);
  const tiles: ChassisTile[] = visibleTiles.map(id => {
    const t = TILE_REGISTRY[id]!;
    return {
      id,
      label: t.label,
      motif: t.motif,
      path: t.path,
      href: t.path,
      comingSoon: t.comingSoon,
      hint: t.hint,
      metrics: metricsFor(id, {
        waiting,
        openMatters: openMatters.data?.length,
        compliance: complianceSummary.data,
        unread: unread.data?.unread,
        conversations: unread.data?.threads,
        members: members.data?.length,
        documentsNeedingAction: documentsNeedingAction.data?.count,
        openTasks: openTaskCount,
        myTasks: myTaskCount,
      }),
    };
  });

  /** Only what this person may actually create. The server refuses regardless;
   *  this is about not OFFERING what cannot be done. */
  const addActions = ADD_DESTINATIONS.filter(a => !a.adminOnly || me.canConfigure);

  /**
   * Ask Mira — this committee's own assistant, and nobody else's.
   *
   * Whether the bar is a real button comes from the SERVER, not from a
   * constant here. Until an API key reaches that process there is nothing
   * behind it, and the bar says so in the same words the server used rather
   * than opening a panel onto nothing.
   *
   * While the status is still loading the bar stays honest too: it says it is
   * checking, which is true, instead of offering a button that may be about to
   * turn out not to work.
   */
  /**
   * One question, one answer, no conversation.
   *
   * Deliberately stateless: each question is answered from the records alone,
   * with no memory of the last one. A committee assistant that remembered
   * would be the first step towards the shared-memory thing this explicitly is
   * not.
   */
  const askMira: AskMiraHandler = async question => {
    const res = await api.askAssistant(question);
    return {
      answer: res.answer,
      sources: res.sources,
      grounded: res.grounded,
      truncated: res.truncated,
    };
  };

  return (
    /* The panel reads its handler from context, so the provider has to sit
       ABOVE the frame that renders it. */
    <AskMiraContext.Provider value={askMira}>
      <NulifeApp
        /* The platform's own palette, not a second one. Nobody has said what
           the hall looks like, and inventing a palette for it is exactly the
           "new visual language" this rebuild exists to undo. */
        theme="farndale"
        /* Real when the server says it is, and honestly absent when it is not.
           The bar is furniture on every home screen in this design language
           either way. */
        assistantUnavailable={assistantUnavailable}

        /* ── Branding ─────────────────────────────────────────────────── */
        // The organisation's own name, from the database — so this is right
        // for the next committee on this platform without a change here.
        title={me.organisationName ?? config.organisationName ?? "Memorial Hall"}
        // NO LOGO. Drea, 2026-09-24: title text only, no icon. The building
        // emoji was a stand-in nobody asked for. The chassis's `logo` slot
        // still exists and still takes an <img> the day the hall has a real
        // mark — an absent logo renders no wrapper at all, so the <h1> sits
        // exactly where Farndale's does.
        // Who they are signed in as, and as what. A committee member may sit
        // on several of these and hold a different role in each.
        subtitle={me.name
          ? `Signed in as ${me.name}${me.role ? ` · ${me.role}` : ""}`
          : undefined}

        /* ── Content ──────────────────────────────────────────────────── */
        liveStatus={liveStatus}
        liveStatusLabel="Today in the hall:"
        liveStatusEmptyMessage="Nothing booked today"
        liveStatusPath="/calendar"
        pulse={pulseItems}
        pulseState={pulseState}
        tiles={tiles}
        modules={MODULE_TILES.map(id => ({
          id, label: TILE_REGISTRY[id]!.label, path: TILE_REGISTRY[id]!.path!,
        }))}
        // The ADD control is a quick-action doorway, not direct navigation to
        // one Add screen — a permission-aware menu of what THIS user may
        // create right now. Actions they can't perform are absent from the
        // list, not disabled in it.
        add={{
          actions: addActions.length
            ? { status: "ready", data: addActions.map(a => ({ id: a.id, label: a.label })) }
            : { status: "empty" },
          onSelect: id => {
            const dest = ADD_DESTINATIONS.find(a => a.id === id);
            if (dest) onGo(dest.path);
          },
        }}
        // NO SECOND ACTION. Drea, 2026-09-24: remove the FILE control
        // entirely for now — it was never placed anywhere deliberately. ADD
        // stands alone, as Farndale's hero does. Filing a document is still
        // reachable: it is in the ADD menu, and the DOCUMENTS tile opens the
        // library. Nothing became unreachable by removing this.
        onNavigate={onGo}

        /* ── This app's own blocks around the grid ────────────────────── */
        beforeTiles={mustAck.length > 0 && (
          <section
            data-testid="must-acknowledge"
            className="mb-3 flex flex-col gap-2.5 p-4"
            style={{
              background: "var(--nl-bg-raised)",
              border: "1px solid var(--nl-status-attention)",
              borderRadius: "var(--nl-radius)",
            }}
          >
            <strong style={{ color: "var(--nl-ink)" }}>
              {mustAck.length === 1
                ? "One thing needs your acknowledgement"
                : `${mustAck.length} things need your acknowledgement`}
            </strong>
            <ul className="flex list-none flex-col gap-2.5 p-0" style={{ margin: 0 }}>
              {mustAck.map(a => (
                <li key={a.id} className="flex flex-wrap items-center gap-2.5">
                  <span className="flex-1" style={{ minWidth: 180 }}>
                    <span style={{ fontWeight: 600, color: "var(--nl-ink)" }}>{a.title}</span>
                    {a.body && (
                      <span className="block text-sm" style={{ color: "var(--nl-ink-dim)" }}>{a.body}</span>
                    )}
                  </span>
                  <button
                    type="button"
                    data-testid="acknowledge"
                    onClick={async () => { await api.acknowledgeAlert(a.id); alerts.reload(); }}
                    className="nl-light-card px-4 py-2 text-sm"
                    style={{
                      minHeight: "var(--nl-hit-min)", fontWeight: 600,
                      borderRadius: "var(--nl-radius-sm)",
                      border: "1px solid var(--nl-band-stroke)",
                      background: "var(--nl-band)",
                      cursor: "pointer",
                    }}
                  >Acknowledge</button>
                </li>
              ))}
            </ul>
            {/* Said plainly: this is not a "dismiss". Somebody pressing it is
                recording that they have seen and accepted it, and the person who
                sent it can see exactly who has and who has not. */}
            <span className="text-xs" style={{ color: "var(--nl-ink-faint)" }}>
              Acknowledging is recorded against your name, and only clears it for you.
            </span>
          </section>
        )}
        afterTiles={
          <div
            className="mt-4 flex flex-wrap items-center gap-3 text-sm"
            style={{ color: "var(--nl-ink-faint)" }}
          >
            <span>
              {/* Counted, not typed. It said "five" for a fortnight and was right;
                  it would have gone on saying five the day a sixth was added. */}
              {HOME_TILES.filter(id => TILE_REGISTRY[id]!.comingSoon).length} of these
              {" "}have nothing behind them yet — they are the plan, not the product.
            </span>
            {/* Beside Sign out because that is where somebody looks for
                "me": the one other thing on this screen that is about you
                rather than the committee. 2026-10-03. */}
            <button
              type="button"
              data-testid="my-profile-link"
              onClick={() => onGo("/profile")}
              className="ml-auto text-xs uppercase tracking-wide"
              style={{
                background: "none", border: 0, padding: 0,
                color: "var(--nl-ink-dim)", cursor: "pointer",
              }}
            >My profile</button>
            <button
              type="button"
              data-testid="sign-out"
              onClick={async () => { await api.signOut(); onSignedOut(); }}
              className="text-xs uppercase tracking-wide"
              style={{
                background: "none", border: 0, padding: 0,
                color: "var(--nl-ink-dim)", cursor: "pointer",
              }}
            >Sign out</button>
          </div>
        }
      />
    </AskMiraContext.Provider>
  );
}

/**
 * The live numbers a tile carries, if any.
 *
 * Kept out of the registry because a registry entry is configuration — a
 * label, a picture and a destination — while this is a lookup into whatever
 * happens to have loaded. A tile with no number is the normal case, not a tile
 * that is still loading.
 */
function metricsFor(
  id: string,
  d: {
    waiting: (kind: string) => number | undefined;
    openMatters: number | undefined;
    compliance: { needsAttention: number; dueSoon: number } | null;
    unread: number | undefined;
    conversations: number | undefined;
    members: number | undefined;
    documentsNeedingAction: number | undefined;
    openTasks: number | undefined;
    myTasks: number | undefined;
  },
) {
  if (id === "messages") {
    // "Unread" only when there is something unread — a permanent "Unread: 0"
    // is how people learn to stop reading a tile. "Conversations" is the
    // steady number that says the tile is working at all.
    return [
      ...(d.unread ? [{ label: "Unread", value: d.unread }] : []),
      { label: "Conversations", value: d.conversations },
    ];
  }
  if (id === "tasks") {
    return [
      { label: "Open", value: d.openTasks },
      ...(d.myTasks ? [{ label: "Yours", value: d.myTasks }] : []),
    ];
  }
  if (id === "members") {
    return [{ label: "On the committee", value: d.members }];
  }
  if (id === "records") {
    // Was red (2026-09-27); Drea, 2026-10-08: red cannot be read on the tile,
    // make it white. So no `attention` flag -- that is what draws it red.
    const n = d.documentsNeedingAction;
    return n ? [{ label: "Needs action", value: n }] : [];
  }
  if (id === "decisions") {
    const n = d.waiting("proposal");
    return [
      { label: "Open", value: d.openMatters },
      ...(n ? [{ label: "Waiting", value: n }] : []),
    ];
  }
  if (id === "compliance" && d.compliance) {
    return [
      { label: "Needs attention", value: d.compliance.needsAttention },
      ...(d.compliance.dueSoon > 0 ? [{ label: "Due soon", value: d.compliance.dueSoon }] : []),
    ];
  }
  return [];
}
